// Contexto ativo derivado de `/me` + store (espelho da rota) e ações de troca (regra 6 de 14.7).
import type { MeContext, MeSite } from '@giyus/api-client';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router';

import { useMe } from '../auth/authContext';
import { invalidatePurposeForContext } from '../purpose/purposeStore';
import {
  browserPreferenceStorage,
  readContextPreference,
  writeContextPreference,
} from './contextPreferences';
import {
  ROUTES,
  findContext,
  findSite,
  hasNetworkAccess,
  resolveEffectiveScope,
} from './contextResolver';
import { contextKey, useContextStore } from './contextStore';

/** Leitura do contexto ativo (sem ações de navegação — utilizável fora do roteador). */
export interface ActiveContextSnapshot {
  readonly hasNetworkAccess: boolean;
  readonly isNetworkScope: boolean;
  readonly context: MeContext | null;
  readonly site: MeSite | null;
  readonly studyCode: string | null;
  /** Fuso do site ativo (ou do único site do tenant); `null` fora de um tenant. */
  readonly timeZoneId: string | null;
  readonly contexts: readonly MeContext[];
}

export interface ActiveContext extends ActiveContextSnapshot {
  readonly switchTenant: (tenantCode: string) => void;
  readonly selectSite: (siteId: string | null) => void;
  readonly goToNetwork: () => void;
}

/** Contexto ativo derivado de `/me` + store; não depende do roteador (usado por `useDateFormatter`). */
export function useActiveContextSnapshot(): ActiveContextSnapshot {
  const me = useMe();
  const storeIsNetworkScope = useContextStore((s) => s.isNetworkScope);
  const storeActiveTenantCode = useContextStore((s) => s.activeTenantCode);
  const activeSiteId = useContextStore((s) => s.activeSiteId);
  const activeStudyCode = useContextStore((s) => s.activeStudyCode);

  const effective = useMemo(() => {
    const remembered = readContextPreference(browserPreferenceStorage(), me.user.id);
    return resolveEffectiveScope(me, storeIsNetworkScope, storeActiveTenantCode, remembered);
  }, [me, storeIsNetworkScope, storeActiveTenantCode]);

  const isNetworkScope = effective.isNetworkScope;
  const activeTenantCode = effective.activeTenantCode;

  const context = useMemo(
    () => (isNetworkScope ? null : (findContext(me, activeTenantCode) ?? null)),
    [me, isNetworkScope, activeTenantCode],
  );
  const site = useMemo(
    () => findSite(context ?? undefined, activeSiteId ?? effective.activeSiteId),
    [context, activeSiteId, effective.activeSiteId],
  );
  const timeZoneId =
    site?.timeZoneId ??
    (context?.sites.length === 1 ? (context.sites[0]?.timeZoneId ?? null) : null);

  return {
    hasNetworkAccess: hasNetworkAccess(me),
    isNetworkScope,
    context,
    site,
    studyCode: activeStudyCode,
    timeZoneId,
    contexts: me.contexts,
  };
}

export function useActiveContext(): ActiveContext {
  const me = useMe();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const snapshot = useActiveContextSnapshot();
  const { context } = snapshot;
  const selectSiteInStore = useContextStore((s) => s.selectSite);
  const mirror = useContextStore((s) => s.mirror);

  const switchTenant = useCallback(
    (tenantCode: string) => {
      const target = findContext(me, tenantCode);
      if (!target) {
        return;
      }
      // Regra 6: trocar tenant limpa site/estudo, esvazia todo o cache de consultas e navega para /t/{code}.
      queryClient.clear();
      mirror({
        isNetworkScope: false,
        activeTenantCode: target.code,
        activeTenantId: target.tenantId,
        activeSiteId: null,
        activeStudyCode: null,
      });
      invalidatePurposeForContext(contextKey({ ...useContextStore.getState() }));
      writeContextPreference(browserPreferenceStorage(), me.user.id, {
        tenantCode: target.code,
        siteId: null,
      });
      void navigate(ROUTES.tenant(target.code));
    },
    [me, queryClient, mirror, navigate],
  );

  const selectSite = useCallback(
    (siteId: string | null) => {
      selectSiteInStore(siteId);
      if (context) {
        writeContextPreference(browserPreferenceStorage(), me.user.id, {
          tenantCode: context.code,
          siteId,
        });
      }
    },
    [selectSiteInStore, context, me.user.id],
  );

  const goToNetwork = useCallback(() => {
    if (!hasNetworkAccess(me)) {
      return;
    }
    queryClient.clear();
    void navigate(ROUTES.network);
  }, [me, queryClient, navigate]);

  return {
    ...snapshot,
    switchTenant,
    selectSite,
    goToNetwork,
  };
}
